import { mergeStyles } from '@cian/utils';
import * as React from 'react';

import styles from './Gallery.css';
import { IFotoramaAPI, IFotoramaData } from '../../../../typings/fotorama';
import { loadFotorama } from '../../../utils/loadFotorama';
import { ArrowRigthIcon, ArrowLeftIcon, FullScreenIcon } from '../../common/icons';

export interface IGalleryStateProps {
  slides: IFotoramaData[];
  onSlideChange?(slideIndex: number): void;
  onInit?(): void;
  startWithIndex?: number;
  isFat?: boolean;
}

interface IGalleryState {
  currentIndex: number;
}

type TGalleryProps = IGalleryStateProps;

export class Gallery extends React.PureComponent<TGalleryProps, IGalleryState> {
  private fotoramaRef: React.RefObject<HTMLDivElement | null> | undefined = React.createRef();
  private rootRef: React.RefObject<HTMLDivElement | null> | undefined = React.createRef();
  // Создаются асинхронно после loadFotorama: до этого и при сбое загрузки они не определены
  private $fotorama: {
    on(type: string, cb: (e: Event, fotorama: IFotoramaAPI) => void): void;
    data(name: string): IFotoramaAPI;
    unbind(): void;
    show(id: number): void;
  };
  private fotorama: IFotoramaAPI;
  private isSwiped = false;

  public state = {
    currentIndex: this.props.startWithIndex || 0,
  };

  public async componentDidMount() {
    await loadFotorama();

    if (window.$ && this.fotoramaRef && this.fotoramaRef.current) {
      this.fotoramaRef.current.addEventListener('touchstart', this.onTouchStartHandler, true);
      this.fotoramaRef.current.addEventListener('touchmove', this.onTouchMoveHandler, true);
      this.fotoramaRef.current.addEventListener('touchend', this.onTouchEndHandler, true);

      const { slides, onSlideChange, onInit } = this.props;

      this.$fotorama = window
        .$(this.fotoramaRef.current)
        .on('fotorama:ready', () => {
          if (onInit) {
            onInit();
          }
        })
        .fotorama({
          data: slides,
          startindex: 0,
        });

      this.fotorama = this.$fotorama.data('fotorama');

      this.$fotorama.on('fotorama:show', (e, fotorama: IFotoramaAPI) => {
        const { currentIndex } = this.state;

        this.setState({
          currentIndex: fotorama.activeIndex,
        });

        if (onSlideChange && fotorama.activeIndex !== currentIndex) {
          onSlideChange(fotorama.activeIndex);
        }
      });
    }
  }

  public componentDidUpdate(prevProps: TGalleryProps) {
    if (typeof this.props.startWithIndex === 'number' && prevProps.startWithIndex !== this.props.startWithIndex) {
      this.fotorama?.show(this.props.startWithIndex as number);
    }
  }

  public componentWillUnmount() {
    this.$fotorama?.unbind();

    if (this.fotoramaRef && this.fotoramaRef.current) {
      this.fotoramaRef.current.removeEventListener('touchstart', this.onTouchStartHandler, true);
      this.fotoramaRef.current.removeEventListener('touchmove', this.onTouchMoveHandler, true);
      this.fotoramaRef.current.removeEventListener('touchend', this.onTouchEndHandler, true);
    }
  }

  public render() {
    const { slides, startWithIndex = 0, isFat } = this.props;

    return (
      <div ref={this.rootRef}>
        <div
          className={styles['fotorama']}
          data-width="100%"
          data-ratio={isFat ? '16/17' : '16/10'}
          data-minheight="214px"
          data-maxheight={isFat ? '680px' : '320px'}
          data-nav="false"
          data-fit="cover"
          data-click="false"
          data-loop="true"
          data-arrows="false"
          data-swipe="true"
          data-auto="false"
          data-allowfullscreen="false"
          data-startindex={startWithIndex}
          ref={this.fotoramaRef}
          onTouchEnd={this.onEventStopPropagation}
        />
        <div
          className={styles['controls']}
          onTouchEnd={this.onEventStopPropagation}
          onMouseUp={this.onEventStopPropagation}
        >
          <div className={styles['left_block']}>
            <div className={styles['arrow_left']} onTouchEnd={this.onClickLeft}>
              <ArrowLeftIcon />
            </div>
          </div>
          <div {...mergeStyles(styles['right_block'], isFat && styles['right_block_fat'])}>
            {!isFat && (
              <div className={styles['fullscreen']}>
                <FullScreenIcon />
              </div>
            )}
            <div className={styles['arrow_right']} onTouchEnd={this.onClickRight}>
              <ArrowRigthIcon />
            </div>
            {!isFat && (
              <div className={styles['counter']}>
                {this.state.currentIndex + 1} / {this.fotorama ? this.fotorama.size : slides.length}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  private onClickLeft = (e: React.TouchEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    this.fotorama?.show('<');
  };

  private onClickRight = (e: React.TouchEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    this.fotorama?.show('>');
  };

  private onTouchStartHandler = () => {
    this.isSwiped = false;
  };

  private onTouchMoveHandler = () => {
    this.isSwiped = true;
  };

  private onEventStopPropagation = (e: React.TouchEvent<HTMLDivElement> | React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
  };

  private onTouchEndHandler = (e: TouchEvent) => {
    if (!this.isSwiped) {
      e.stopPropagation();
      e.preventDefault();
      if (this.rootRef) {
        const event = new Event('touchend', { bubbles: true });
        (this.rootRef.current as HTMLElement).dispatchEvent(event);
      }
    }
  };
}
